import { action, computed, observable } from "mobx";
import { SystemComponent } from "../models/outage";
import NotificationService from "./notificationService";
import LocalizationService from "./localizationService";
import OutageRepository from "../repositories/outageRepository";
import differenceInMilliseconds from "date-fns/differenceInMilliseconds";
import isSameDay from "date-fns/isSameDay";
import { deserializeArray } from "class-transformer";
import LanguageCode from "../models/languageCode";
import SystemComponentOutageResponse from "entities/systemComponentOutageResponse";
import SystemComponentOutagesResponse from "entities/systemComponentOutagesResponse";

export enum OutageStatus {
  PLANNED = "planned",
  VISIBLE = "visible",
  ACTIVE = "active",
  PASSED = "passed",
}

export default class OutageService {
  static validSinceRegex = /%validSince%/g;
  static validUntilRegex = /%validUntil%/g;
  static cacheStoreKey = "outagesCache";
  static closedItemsStoreKey = "closedOutages";

  @observable _isInitialized = false;
  @observable outages: SystemComponentOutageResponse[];
  @observable private closedOutages: SystemComponentOutageResponse[] = [];
  // Clock is updated when some action happens
  @observable time = new Date();

  @observable activeOutagesPerComponent: Map<SystemComponent, SystemComponentOutageResponse> = new Map<
    SystemComponent,
    SystemComponentOutageResponse
  >();

  @computed
  get isInitialized() {
    return this._isInitialized;
  }

  @computed
  get visibleOutages() {
    if (!this.outages?.length) {
      return [];
    }
    return this.outages?.filter(
      (outage) =>
        this.time >= outage.visibleSince && (!outage.validUntil || (outage.validUntil && this.time <= outage.validUntil))
    );
  }

  @computed
  get otherVisibleOutages() {
    return this.visibleOutages.filter((outage) => {
      const component = this.recognizeComponent(outage);

      return (
        (component === SystemComponent.OTHER || component === SystemComponent.ESHOP) &&
        !this.closedOutages.find((item) => item.id === outage.id)
      );
    });
  }

  @computed
  get payGateVisibleOutages() {
    return this.visibleOutages.filter((outage) => {
      const component = this.recognizeComponent(outage);

      return component === SystemComponent.PAY_GATE;
    });
  }

  @computed
  get isdsVisibleOutages() {
    return this.visibleOutages.filter((outage) => {
      const component = this.recognizeComponent(outage);

      return component === SystemComponent.ISDS;
    });
  }

  @computed
  get payGateActiveOutage() {
    return this.activeOutagesPerComponent.get(SystemComponent.PAY_GATE);
  }

  @computed
  get isdsActiveOutage() {
    return this.activeOutagesPerComponent.get(SystemComponent.ISDS);
  }

  @computed
  get eshopActiveOutage() {
    return this.activeOutagesPerComponent.get(SystemComponent.ESHOP);
  }

  constructor(
    private repository: OutageRepository,
    public notificationService: NotificationService,
    public localizationService: LocalizationService
  ) {
    const cachedOutages = sessionStorage.getItem(OutageService.cacheStoreKey);
    this.closedOutages = deserializeArray(
      SystemComponentOutageResponse,
      sessionStorage.getItem(OutageService.closedItemsStoreKey) ?? "[]"
    );

    if (cachedOutages) {
      this.outages = deserializeArray(SystemComponentOutageResponse, cachedOutages);
    }
  }

  async initialize() {
    const response = await this.repository.getSystemComponentOutages();
    if (response instanceof SystemComponentOutagesResponse && response.items.length > 0) {
      this.outages = response.items;
    }
    sessionStorage.setItem(OutageService.cacheStoreKey, JSON.stringify(response));
    this.planClockTicks();
    this._isInitialized = true;
  }

  getMessage(outage: SystemComponentOutageResponse) {
    const status = this.getOutageStatus(outage);

    // Do we have translated messages from API?
    if (outage.localizations) {
      let localizedMessage;
      // Try to get message in current language
      localizedMessage = outage.localizations.find((outage) => outage.languageCode === this.localizationService.languageCode);
      // Otherwise try to get English message
      if (!localizedMessage) {
        localizedMessage = outage.localizations.find((outage) => outage.languageCode === LanguageCode.En);
      }
      if (localizedMessage && localizedMessage.description?.length > 0) {
        return localizedMessage.description;
      }
    }

    // Build default message for appropriate component
    let message = this.localizationService.translateGeneral(
      `outages.${outage.systemComponentCode}.${status}.${outage.validUntil ? "validUntil" : "validInfinity"}`,
      undefined,
      true
    );
    let sameDay = false;

    if (outage.validUntil) {
      sameDay = isSameDay(outage.validUntil, outage.validSince);
      message = message.replace(
        OutageService.validUntilRegex,
        sameDay && status === OutageStatus.VISIBLE
          ? this.localizationService.formatTime(outage.validUntil)
          : this.localizationService.formatDateTime(outage.validUntil)
      );
    }

    message = message.replace(OutageService.validSinceRegex, this.localizationService.formatDateTime(outage.validSince));

    if (outage.description && this.localizationService.locale === "cs") {
      message += `  \r\n${outage.description}`;
    }

    return message;
  }

  getOutageStatus(outage: SystemComponentOutageResponse) {
    if (outage.visibleSince > this.time) {
      return OutageStatus.PLANNED;
    } else if (outage.visibleSince <= this.time && outage.validSince > this.time) {
      return OutageStatus.VISIBLE;
    } else if (
      outage.validSince <= this.time &&
      ((outage.validUntil && outage.validUntil >= this.time) || outage.validUntil === undefined)
    ) {
      return OutageStatus.ACTIVE;
    } else {
      return OutageStatus.PASSED;
    }
  }

  isActive(outage?: SystemComponentOutageResponse) {
    if (!outage) {
      return false;
    }

    return outage.validSince <= this.time && (!outage.validUntil || (outage.validUntil && this.time <= outage.validUntil));
  }

  recognizeComponent(outage: SystemComponentOutageResponse) {
    if (
      ([SystemComponent.PAY_GATE, SystemComponent.ISDS, SystemComponent.ESHOP] as string[]).includes(outage.systemComponentCode)
    ) {
      return outage.systemComponentCode as SystemComponent;
    } else {
      return SystemComponent.OTHER;
    }
  }

  @action.bound
  closeOutage(outage: SystemComponentOutageResponse) {
    this.closedOutages.push(outage);
    sessionStorage.setItem(OutageService.closedItemsStoreKey, JSON.stringify(this.closedOutages));
  }

  private planClockTicks() {
    this.visibleOutages.forEach((visibleOutage) => {
      this.activateAndDeactivateOutage(visibleOutage);

      if (visibleOutage.validSince > this.time) {
        setTimeout(this.updateClocks, differenceInMilliseconds(visibleOutage.validSince, this.time));
      }

      if (visibleOutage.validUntil) {
        setTimeout(this.updateClocks, differenceInMilliseconds(visibleOutage.validUntil, new Date()));
      }
    });
  }

  @action
  private activateAndDeactivateOutage(visibleOutage: SystemComponentOutageResponse) {
    const systemComponent = this.recognizeComponent(visibleOutage);
    if (systemComponent !== SystemComponent.OTHER) {
      if (this.time >= visibleOutage.validSince) {
        this.activeOutagesPerComponent.set(systemComponent, visibleOutage);
      } else {
        setTimeout(() => {
          this.activeOutagesPerComponent.set(systemComponent, visibleOutage);
        }, differenceInMilliseconds(visibleOutage.validSince, this.time));
      }

      if (visibleOutage.validUntil) {
        setTimeout(() => {
          this.activeOutagesPerComponent.delete(systemComponent);
        }, differenceInMilliseconds(visibleOutage.validUntil, new Date()));
      }
    }
  }

  @action.bound
  private updateClocks() {
    this.time = new Date();
  }
}
